// Shared business-unit + activity catalog.
//
// These two lists change rarely (only an admin edits them in Settings) but
// were being re-fetched independently in ~10 components, each re-implementing
// the same useState + useEffect. This hook fetches them once per session,
// caches at module scope so every consumer shares one network call, and hands
// back empty arrays until the first load resolves.
//
// After mutating the catalog (Settings add/delete a BU or activity), call
// refreshOrgCatalog() to invalidate the cache so the next read re-fetches.
import { useEffect, useState } from "react";
import { api } from "../api/client";
let cache = null;
let inflight = null;
async function load() {
    const [businessUnits, activities] = await Promise.all([
        api.get("/orgs/business-units"),
        api.get("/orgs/activities"),
    ]);
    cache = { businessUnits, activities };
    return cache;
}
/** Drop the cached catalog so the next `useOrgCatalog()` read re-fetches.
 *  Call after editing business units / activities in admin Settings. */
export function refreshOrgCatalog() {
    cache = null;
    inflight = null;
}
/** Shared BU + activity catalog. Cached across components; returns empty
 *  arrays until the first load resolves. `loading` is true only while that
 *  initial fetch is in flight. */
export function useOrgCatalog() {
    const [data, setData] = useState(cache);
    const [loading, setLoading] = useState(cache === null);
    useEffect(() => {
        if (cache) {
            setData(cache);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        (inflight ??= load())
            .then((c) => {
            if (!cancelled) {
                setData(c);
                setLoading(false);
            }
        })
            .catch(() => {
            // Swallow — consumers render empty dropdowns; a later mount retries.
            inflight = null;
            if (!cancelled)
                setLoading(false);
        });
        return () => {
            cancelled = true;
        };
    }, []);
    return {
        businessUnits: data?.businessUnits ?? [],
        activities: data?.activities ?? [],
        loading,
    };
}
